import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import CompanyNameForm from '@/features/dashboard/company/components/CompanyNameForm.tsx';
import useUpdateCompanyInfo from '@/features/dashboard/company/hooks/useUpdateCompanyInfo.ts';
import {
  companyNameSchema,
  CompanyNameFormData,
} from '@/features/dashboard/company/schemas/companyNameSchema.ts';
import CompanyInfoDto from '@/features/dashboard/company/types/CompanyInfoDto.ts';
import { FileAction } from '@/schemas/fileFieldSchema.ts';

interface EditCompanyNameModalProps {
  companyInfo: CompanyInfoDto;
  onClose: () => void;
}

const EditCompanyNameModal = ({
  companyInfo,
  onClose,
}: EditCompanyNameModalProps) => {
  const updateMutation = useUpdateCompanyInfo();

  const methods = useForm<CompanyNameFormData>({
    resolver: zodResolver(companyNameSchema),
    values: { companyName: companyInfo.companyName },
  });

  const { handleSubmit } = methods;

  // Zapis idzie jednym PUT-em na całą firmę, więc logo jedzie z nim nietknięte.
  const onSubmit = (data: CompanyNameFormData) => {
    updateMutation.mutate(
      {
        data: {
          companyName: data.companyName,
          logo: {
            action: FileAction.NO_ACTION,
            fileId: companyInfo.companyLogoFileId ?? undefined,
            fileUrl: companyInfo.companyLogoDownloadUrl ?? undefined,
          },
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Modal onClose={onClose} className="max-w-[520px]">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader title="Edytuj nazwę firmy" onClose={onClose} />
          <ModalContent>
            <CompanyNameForm />
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={updateMutation.isPending}
            >
              Zapisz
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditCompanyNameModal;
